/**
 * Placement libre du texte sur le vêtement, guidé par des repères.
 *
 * Le client pose son texte où il veut, mais ne part jamais d'une page blanche :
 * des repères le déposent aux emplacements courants — poitrine centrée,
 * poitrine gauche, grand dos — et de là il déplace, agrandit, recommence.
 * Libre, mais aiguillé.
 *
 * Tout ce qui relève du placement — l'échelle du vêtement, les bornes, le
 * déplacement, l'écriture de la commande — vit dans `version-placement.js`,
 * partagé avec l'outil des visuels. Ne reste ici que ce qui tient aux lettres :
 * les ajuster à la largeur demandée, les étirer, choisir l'encre, et poser sur
 * elles le relief du coton.
 */

import { Placement, souvenir, borner, boiteFormat } from '@version/placement';

/** Les dix encres, à l'identique du produit SWAG. */
const ENCRES = {
  Blanc: '#ffffff', Noir: '#0a0a0a', Gris: '#787878', Bleu: '#006eff', Vert: '#00aa50',
  Jaune: '#ffd21f', Orange: '#ff8c00', Rouge: '#e11e28', Rose: '#ff2d95', Violet: '#9632d2',
};
const INVERSE = { Noir: 'Blanc', Blanc: 'Noir' };

const DEPART = { x: 0, y: 8, largeur: 21, face: 'devant' };

class VersionTextPreview extends HTMLElement {
  connectedCallback() {
    this.champ = this.querySelector('[data-version-texte-champ]');
    this.mot = this.querySelector('[data-version-texte-mot]');
    this.relief = this.querySelector('[data-version-texte-relief]');
    this.zone = this.querySelector('[data-version-texte-zone]');
    this.scene = this.querySelector('[data-version-texte-scene]');
    this.compteur = this.querySelector('[data-version-texte-compteur]');
    this.lecture = this.querySelector('[data-version-texte-lecture]');
    this.couleurs = [...this.querySelectorAll('[data-version-texte-couleur]')];
    this.faces = [...this.querySelectorAll('[data-version-texte-face-choix]')];
    this.reperes = this.querySelector('[data-version-texte-reperes]');
    this.sorties = {
      face: this.querySelector('[data-sortie="face"]'),
      position: this.querySelector('[data-sortie="position"]'),
      taille: this.querySelector('[data-sortie="taille"]'),
    };
    if (!this.champ || !this.mot || !this.zone || !this.scene) return;

    this.memoire = souvenir('texte', DEPART);
    this.placement = new Placement({
      hote: this,
      scene: this.scene,
      zone: this.zone,
      sure: this.querySelector('[data-version-texte-sure]'),
      memoire: this.memoire,
    });

    this.rafraichir = this.rafraichir.bind(this);

    this.champ.addEventListener('input', this.rafraichir);
    document.addEventListener('change', this.rafraichir);
    for (const bouton of this.faces) {
      bouton.addEventListener('click', () => {
        this.memoire.face = bouton.dataset.face;
        this.rafraichir();
      });
    }
    this.placement.brancherReperes(this.reperes, () => this.rafraichir());
    this.placement.brancherDeplacement(this.rafraichir);

    /* Anton n'est pas la police du site : tant qu'elle n'est pas arrivée, le
       texte est mesuré dans une autre fonte et l'échelle serait fausse. */
    if (document.fonts?.ready) document.fonts.ready.then(this.rafraichir);
    this.observateur = new ResizeObserver(this.rafraichir);
    this.observateur.observe(this.scene);

    this.rafraichir();
  }

  disconnectedCallback() {
    this.champ?.removeEventListener('input', this.rafraichir);
    document.removeEventListener('change', this.rafraichir);
    this.observateur?.disconnect();
  }

  /** Empêche la correction de largeur de se rappeler elle-même sans fin. */
  #corrige = false;

  /**
   * La plus grande hauteur acceptable : celle de la feuille de transfert, ou
   * celle qui reste entre le col et l'ourlet si le vêtement est plus court.
   */
  #plafondHauteur({ g, scene, pxParCm }) {
    return Math.min(boiteFormat().hauteur, this.placement.hauteurUtile(g, scene, pxParCm));
  }

  /** La hauteur réelle des lettres, en centimètres sur le vêtement choisi. */
  #hauteurCm(pxParCm, facteurTaille) {
    if (!pxParCm) return 0;
    return this.mot.getBoundingClientRect().height / pxParCm / facteurTaille;
  }

  /**
   * Retire du choix l'encre de la couleur du vêtement — elle serait invisible —
   * et rebascule dessus si c'était celle qui était cochée.
   */
  #choisirEncre(couleurVetement) {
    if (this.couleurs.length === 0) return;
    for (const radio of this.couleurs) {
      const etiquette = radio.closest('label');
      if (etiquette) etiquette.hidden = radio.value === couleurVetement;
    }
    let coche = this.couleurs.find((radio) => radio.checked);
    if (!coche || coche.value === couleurVetement) {
      const repli =
        this.couleurs.find((radio) => radio.value === INVERSE[couleurVetement]) ??
        this.couleurs.find((radio) => radio.value !== couleurVetement);
      if (repli) { repli.checked = true; coche = repli; }
    }
    const encre = ENCRES[coche?.value];
    if (encre) this.scene.style.setProperty('--version-texte-encre', encre);
  }

  rafraichir() {
    /* Les majuscules s'appliquent au champ lui-même, pas seulement à l'aperçu :
       c'est la commande qui part à l'impression, elle doit porter ce que le
       client a vu. La transformation garde la longueur du texte saisi. */
    if (this.dataset.majuscules !== 'false') {
      const majuscule = this.champ.value.toLocaleUpperCase('fr');
      if (majuscule !== this.champ.value && majuscule.length === this.champ.value.length) {
        const debut = this.champ.selectionStart;
        const fin = this.champ.selectionEnd;
        this.champ.value = majuscule;
        if (debut !== null) this.champ.setSelectionRange(debut, fin);
      }
    }

    const saisi = this.champ.value.trim();
    this.mot.textContent = saisi || this.dataset.exemple || 'TON TEXTE';
    if (this.relief) this.relief.textContent = this.mot.textContent;
    this.toggleAttribute('data-vide', saisi === '');

    if (this.compteur) {
      const max = Number(this.champ.getAttribute('maxlength')) || 20;
      this.compteur.textContent = `${this.champ.value.length}/${max}`;
    }

    this.dataset.vetement = this.placement.teinte;
    this.dataset.face = this.memoire.face;
    this.#choisirEncre(this.placement.teinte === 'blanc' ? 'Blanc' : 'Noir');
    for (const bouton of this.faces) {
      bouton.setAttribute('aria-pressed', String(bouton.dataset.face === this.memoire.face));
    }
    this.placement.filtrerReperes(this.reperes);

    /* Le format commande seul la taille : on la relit à chaque rendu plutôt
       que de la garder en mémoire, sinon un format changé laisserait l'ancienne
       largeur à l'écran. */
    this.memoire.largeur = boiteFormat().largeur;

    /* Deux passes. La première pose le motif pour connaître sa largeur à
       l'écran, dont dépend le corps des lettres, donc leur hauteur ; la seconde
       recale le tout une fois cette hauteur connue — sans quoi un texte étiré
       dépasserait l'ourlet sans que rien ne l'arrête. */
    const echelle = this.placement.poser();
    if (!echelle) return;
    this.#ajusterTaille();
    let hauteurCm = this.#hauteurCm(echelle.pxParCm, echelle.facteurTaille);

    /* La hauteur d'un texte n'est pas réglée, elle est subie : elle tombe du
       nombre de lettres, de leur dessin et de l'étirement. Deux lettres larges
       tirées à 29 cm et étirées ×3 donnent un motif de plus de 80 cm — plus
       grand que le vêtement. On rabaisse alors la largeur d'autant, ce qui est
       la seule prise qu'on ait dessus. Le corps des lettres est proportionnel à
       la largeur, donc une seule correction suffit, sans boucler. */
    const plafond = this.#plafondHauteur(echelle);
    if (hauteurCm > plafond && !this.#corrige) {
      this.#corrige = true;
      const reduite = borner(Math.floor((this.memoire.largeur * plafond) / hauteurCm), 6, boiteFormat().largeur);
      if (reduite !== this.memoire.largeur) {
        this.memoire.largeur = reduite;
        this.rafraichir();
        this.#corrige = false;
        return;
      }
      this.#corrige = false;
      hauteurCm = plafond;
    }

    this.placement.caler(hauteurCm);
    this.placement.poser();
    this.#calerRelief();

    if (this.lecture) this.lecture.textContent = this.placement.lecture();
    this.placement.noter(this.sorties, hauteurCm);
  }

  /**
   * Fait suivre le format — donc le prix — à la taille réelle du texte.
   *
   * Pour un texte, la hauteur n'est pas réglée mais mesurée : elle tombe du
   * nombre de lettres et de l'étirement. C'est donc la mesure, et non une
   * consigne, qui décide du format.
   */
  /** Met le texte exactement à la largeur demandée. */
  #ajusterTaille() {
    const cadre = this.zone.getBoundingClientRect();
    if (!cadre.width) return;
    const REFERENCE = 100;
    this.mot.style.fontSize = `${REFERENCE}px`;
    const mesure = this.mot.scrollWidth;
    if (!mesure) return;
    this.mot.style.fontSize = `${Math.max(6, (cadre.width / mesure) * REFERENCE)}px`;
  }

  /**
   * Aligne la carte de relief sur la photo.
   *
   * Le texte n'est plus étirable : il garde les proportions de sa fonte. Le
   * réglage « Hauteur des lettres » les forçait jusqu'à ×4,3, et le motif
   * sortait alors du format qu'on venait de facturer.
   */
  #calerRelief() {
    if (!this.relief) return;
    const scene = this.scene.getBoundingClientRect();
    const zone = this.zone.getBoundingClientRect();
    if (!scene.width) return;
    this.relief.style.setProperty('--relief-taille', `${scene.width}px ${scene.height}px`);
    this.relief.style.setProperty('--relief-pos', `${scene.left - zone.left}px ${scene.top - zone.top}px`);
    this.relief.style.fontSize = this.mot.style.fontSize;
  }
}

if (!customElements.get('version-text-preview')) {
  customElements.define('version-text-preview', VersionTextPreview);
}
